/**
 * Sandbox v2 config: base mode + common overrides + per-mode tunables.
 * Legacy presets (no baseMode) migrate to baseMode "normal" with prior fields preserved.
 */

import { normalizeLevelData } from "./speedrun-levels";
import { normalizePenaltyLevelData } from "./penalty-levels";
import { sandboxFieldHelp } from "./sandbox-field-help";

export const SANDBOX_BASE_MODES = [
    "normal",
    "progression",
    "wasd",
    "superdot",
    "roulette",
    "speedrun",
    "highway",
    "darkness",
    "mirror",
    "snake",
    "combo",
    "colorrush",
    "heat",
    "hunter",
    "pop",
    "counter",
    "aim",
    "hook",
    "tilt",
    "orbit",
    "delay",
    "penalty",
] as const;

export type SandboxBaseMode = (typeof SANDBOX_BASE_MODES)[number];

export const SANDBOX_BASE_MODE_TITLES: Record<SandboxBaseMode, string> = {
    normal: "Normal",
    progression: "Progression",
    wasd: "WASD",
    superdot: "Superdot",
    roulette: "Roulette",
    speedrun: "SpeedRun",
    highway: "Highway",
    darkness: "Darkness",
    mirror: "Mirror",
    delay: "Delay",
    snake: "Snake",
    combo: "Combo",
    colorrush: "Color Rush",
    heat: "Heat",
    hunter: "Hunter",
    pop: "POP",
    counter: "Counter",
    aim: "AIM",
    hook: "Hook",
    tilt: "Tilt",
    orbit: "Orbit",
    penalty: "Golf",
};

export function isSandboxBaseMode(id: string): id is SandboxBaseMode {
    return (SANDBOX_BASE_MODES as readonly string[]).includes(id);
}

/** Field metadata for dynamic Sandbox config UI. */
export type SandboxFieldDef =
    | {
          kind: "number";
          key: string;
          label: string;
          min: number;
          max: number;
          step: number;
          hint?: string;
      }
    | { kind: "toggle"; key: string; label: string; hint?: string };

export type SandboxConfig = {
    schemaVersion: 2;
    baseMode: SandboxBaseMode;
    /** Shared overlays / generic tunables (legacy sandbox fields + extras). */
    common: {
        ballsSize: number;
        ballsSpeed: number;
        initialBalls: number;
        ballsPerSpawn: number;
        pointsPerSpawn: number;
        cursorSpeed: number;
        cursorSize: number;
        smoothCursor: number;
        ballCollisionsEnabled: boolean;
        bounceSpeedBoost: number;
        wasdEnabled: boolean;
        progressionEnabled: boolean;
        magnetEnabled: boolean;
        /**
         * Extra spawn clearance around the cursor (reference px, scaled by layout).
         * Official default is 44 — larger = balls spawn farther from the cursor.
         */
        spawnClearance: number;
        /**
         * Internal play flag: always true for baseMode "speedrun", else false.
         * Not shown in the Sandbox UI (SpeedRun Layer toggle removed).
         */
        speedrunMode: boolean;
        speedrunLayout: unknown;
        /** Custom Golf (penalty) layout for Sandbox Customize. */
        penaltyLayout: unknown;
    };
    /** Per-base-mode tunables; only the active baseMode block is required at play time. */
    mode: Record<string, number | boolean | string>;
};

function num(v: unknown, fallback: number): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}

function clamp(n: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, n));
}

export function defaultModeParams(baseMode: SandboxBaseMode): Record<string, number | boolean | string> {
    switch (baseMode) {
        case "normal":
        case "mirror":
        case "delay":
            return {
                initialBalls: 1,
                ballsSpeed: 0.8,
                ballsPerSpawn: 50,
                pointsPerSpawn: 500,
                bounceBoost: 1.08,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "tilt":
            return {
                initialBalls: 1,
                ballsSpeed: 0.8,
                ballsPerSpawn: 25,
                pointsPerSpawn: 500,
                bounceBoost: 1.08,
                tiltSensitivity: 1,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "orbit":
            return {
                initialBalls: 1,
                ballsSize: 14,
                cursorSize: 5,
                coreRadiusFrac: 0.118,
                cursorGapRef: 18,
                angularAccel: 36,
                maxAngularSpeed: 9.5,
                angularDrag: 22,
                ballSpeedBase: 0.52,
                ballSpeedPerTier: 0.2,
                spawnRateBase: 0.48,
                spawnRatePerTier: 0.38,
                livesStart: 3,
                livesCap: 5,
                lifeBonusEvery: 1000,
            };
        case "progression":
            return {
                initialBalls: 5,
                ballsSpeed: 0.8,
                levelSpeedStep: 1.08,
                maxBalls: 256,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "wasd":
            return {
                initialBalls: 5,
                ballsSpeed: 1.2,
                ballsPerSpawn: 5,
                pointsPerSpawn: 500,
                cursorSpeed: 4,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "superdot":
            return {
                initialBalls: 1,
                ballsSpeed: 0.8,
                ballsPerSpawn: 50,
                pointsPerSpawn: 500,
                shieldCooldownMs: 15000,
                slowCooldownMs: 15000,
                barrierCooldownMs: 30000,
                pushCooldownMs: 30000,
                abilityDurationMs: 5000,
                pushRadius: 220,
                barrierRadius: 44,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "roulette":
            return {
                initialBalls: 10,
                ballsSpeed: 0.22,
                ballsPerSpawn: 10,
                spawnEveryMs: 5000,
                spawnEveryPlus100Ms: 10000,
                bounceMult: 1.5,
                effectIntervalMs: 10000,
                enableSizeFx: true,
                enableSpeedFx: true,
                enableVisibilityFx: true,
                enableControlFx: true,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "speedrun":
            return {
                cursorSize: 5,
            };
        case "highway":
            return {
                baseCameraPxS: 78,
                maxSpeedScore: 5000,
                scorePerSpeedTier: 1000,
                speedStepMul: 1.25,
                roadWidthFrac: 0.72,
                cursorSize: 5,
            };
        case "darkness":
            return {
                initialBalls: 5,
                ballsSpeed: 0.85,
                ballsPerSpawn: 5,
                pointsPerSpawn: 500,
                pointCount: 3,
                visionStartFrac: 0.27,
                visionMaxFrac: 0.4,
                visionShrinkBasePxS: 10.2,
                visionShrinkMaxPxS: 22.5,
                visionShrinkScoreCap: 4000,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "snake":
            return {
                appleStartCount: 8,
                appleSpeed: 0.8,
                ballsPerSpawn: 55,
                pointsPerSpawn: 500,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "combo":
            return {
                initialBalls: 12,
                ballsSpeed: 1.15,
                ballsPerSpawn: 60,
                spawnEveryMs: 6667,
                livesMax: 2,
                timeoutMs: 2000,
                bounceBoost: 1.08,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "colorrush":
            return {
                ballsTotal: 350,
                ballSpeed: 0.85,
                cycleMs: 5000,
                respawnDelayMs: 2000,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "heat":
            return {
                initialBalls: 50,
                ballsSpeed: 0.8,
                ballsPerSpawn: 50,
                pointsPerSpawn: 500,
                heatRiseRate: 0.00275,
                heatCoolRate: 0.0042,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "hunter":
            return {
                normalStart: 24,
                normalPerSpawn: 10,
                redPerSpawn: 2,
                normalSpeed: 0.8,
                pointsPerSpawn: 250,
                chaseSpeed: 1.08,
                patrolRadius: 56,
                turnRate: 0.16,
                bounceBoost: 1.08,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "pop":
            return {
                initialBalls: 50,
                cursorSize: 5,
            };
        case "counter":
            return {
                ballsTotal: 100,
                ballSpeed: 0.85,
                roundMs: 5000,
                respawnDelayMs: 2000,
                bounceBoost: 1.08,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "aim":
            return {
                ballsTotal: 30,
                ballSpeed: 0.85,
                roundMs: 2000,
                wavePauseMs: 3000,
                bounceBoost: 1.08,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "hook":
            return {
                normalStart: 15,
                redStart: 15,
                normalPerSpawn: 5,
                redPerSpawn: 10,
                spawnEveryMs: 15000,
                ballSpeed: 0.85,
                flySpeed: 1280,
                retractMs: 520,
                bounceBoost: 1.08,
                cursorSize: 5,
                ballsSize: 14,
            };
        case "penalty":
            return {
                cursorSize: 5,
            };
        default:
            return {};
    }
}

export function defaultCommon(): SandboxConfig["common"] {
    return {
        ballsSize: 14,
        ballsSpeed: 0.8,
        initialBalls: 1,
        ballsPerSpawn: 5,
        pointsPerSpawn: 500,
        cursorSpeed: 4,
        cursorSize: 5,
        smoothCursor: 0,
        ballCollisionsEnabled: false,
        bounceSpeedBoost: 1.1,
        wasdEnabled: false,
        progressionEnabled: false,
        magnetEnabled: false,
        spawnClearance: 44,
        speedrunMode: false,
        speedrunLayout: normalizeLevelData({}),
        penaltyLayout: normalizePenaltyLevelData({}),
    };
}

/**
 * Rule overlays that match each base mode's normal gameplay.
 * Sandbox UI starts from these; players can freely toggle them off/on.
 */
export function defaultRuleOverlaysForBaseMode(
    baseMode: SandboxBaseMode,
): Pick<
    SandboxConfig["common"],
    "ballCollisionsEnabled" | "wasdEnabled" | "progressionEnabled" | "magnetEnabled" | "speedrunMode"
> {
    return {
        // Color Rush / Counter / Hook / AIM / POP normally resolve ball–ball (AIM: golden pairs).
        ballCollisionsEnabled:
            baseMode === "colorrush" ||
            baseMode === "counter" ||
            baseMode === "hook" ||
            baseMode === "aim" ||
            baseMode === "pop",
        wasdEnabled: baseMode === "wasd",
        progressionEnabled: baseMode === "progression",
        magnetEnabled: false,
        speedrunMode: baseMode === "speedrun",
    };
}

export function defaultSandboxConfig(baseMode: SandboxBaseMode = "normal"): SandboxConfig {
    const modeDefaults = defaultModeParams(baseMode);
    const common = defaultCommon();
    const rules = defaultRuleOverlaysForBaseMode(baseMode);
    common.ballCollisionsEnabled = rules.ballCollisionsEnabled;
    common.wasdEnabled = rules.wasdEnabled;
    common.progressionEnabled = rules.progressionEnabled;
    common.magnetEnabled = rules.magnetEnabled;
    common.speedrunMode = rules.speedrunMode;
    // Seed common from mode defaults where overlapping
    if (typeof modeDefaults.ballsSpeed === "number") common.ballsSpeed = modeDefaults.ballsSpeed;
    if (typeof modeDefaults.ballsSize === "number") common.ballsSize = modeDefaults.ballsSize;
    if (typeof modeDefaults.cursorSize === "number") common.cursorSize = modeDefaults.cursorSize;
    if (typeof modeDefaults.initialBalls === "number") common.initialBalls = modeDefaults.initialBalls;
    if (typeof modeDefaults.ballsPerSpawn === "number") common.ballsPerSpawn = modeDefaults.ballsPerSpawn;
    if (typeof modeDefaults.pointsPerSpawn === "number") common.pointsPerSpawn = modeDefaults.pointsPerSpawn;
    return {
        schemaVersion: 2,
        baseMode,
        common,
        mode: { ...modeDefaults },
    };
}

/** Common overlay fields always shown (spawn/size duplicates are omitted when mode already exposes them). */
export const COMMON_FIELD_DEFS: SandboxFieldDef[] = [
    { kind: "number", key: "common.ballsSize", label: "Balls Size", min: 1, max: 1000, step: 1, hint: sandboxFieldHelp("common.ballsSize", "ballsSize") },
    { kind: "number", key: "common.ballsSpeed", label: "Balls Speed", min: 0.1, max: 1000, step: 0.1, hint: sandboxFieldHelp("common.ballsSpeed", "ballsSpeed") },
    { kind: "number", key: "common.initialBalls", label: "Initial Balls", min: 0, max: 500, step: 1, hint: sandboxFieldHelp("common.initialBalls", "initialBalls") },
    { kind: "number", key: "common.ballsPerSpawn", label: "Balls Per Spawn", min: 0, max: 1000, step: 1, hint: sandboxFieldHelp("common.ballsPerSpawn", "ballsPerSpawn") },
    { kind: "number", key: "common.pointsPerSpawn", label: "Points Per Spawn", min: 1, max: 10000, step: 1, hint: sandboxFieldHelp("common.pointsPerSpawn", "pointsPerSpawn") },
    { kind: "number", key: "common.cursorSpeed", label: "Cursor Speed", min: 1, max: 1000, step: 1, hint: sandboxFieldHelp("common.cursorSpeed", "cursorSpeed") },
    { kind: "number", key: "common.cursorSize", label: "Cursor Size", min: 1, max: 1000, step: 1, hint: sandboxFieldHelp("common.cursorSize", "cursorSize") },
    { kind: "number", key: "common.smoothCursor", label: "Smooth Cursor", min: 0, max: 1000, step: 1, hint: sandboxFieldHelp("common.smoothCursor", "smoothCursor") },
    {
        kind: "number",
        key: "common.spawnClearance",
        label: "Spawn Clearance",
        min: 0,
        max: 1000,
        step: 1,
        hint: sandboxFieldHelp("common.spawnClearance", "spawnClearance"),
    },
    { kind: "toggle", key: "common.ballCollisionsEnabled", label: "Ball Collisions", hint: sandboxFieldHelp("common.ballCollisionsEnabled", "ballCollisionsEnabled") },
    { kind: "number", key: "common.bounceSpeedBoost", label: "Bounce Speed Boost (overlay)", min: 0, max: 1000, step: 0.01, hint: sandboxFieldHelp("common.bounceSpeedBoost", "bounceSpeedBoost") },
    { kind: "toggle", key: "common.wasdEnabled", label: "WASD Movement", hint: sandboxFieldHelp("common.wasdEnabled", "wasdEnabled") },
    { kind: "toggle", key: "common.progressionEnabled", label: "Progression Lane", hint: sandboxFieldHelp("common.progressionEnabled", "progressionEnabled") },
    { kind: "toggle", key: "common.magnetEnabled", label: "Magnet", hint: sandboxFieldHelp("common.magnetEnabled", "magnetEnabled") },
];

/** Keys that appear in both mode params and common — prefer a single control (mode section). */
const OVERLAP_COMMON_KEYS = new Set([
    "ballsSize",
    "ballsSpeed",
    "initialBalls",
    "ballsPerSpawn",
    "pointsPerSpawn",
    "cursorSpeed",
    "cursorSize",
]);

export function commonFieldDefsForMode(baseMode: SandboxBaseMode): SandboxFieldDef[] {
    const modeKeys = new Set(Object.keys(defaultModeParams(baseMode)));
    return COMMON_FIELD_DEFS.filter((def) => {
        const key = def.key.split(".")[1] || "";
        if (OVERLAP_COMMON_KEYS.has(key) && modeKeys.has(key)) return false;
        return true;
    });
}

export function modeFieldDefs(baseMode: SandboxBaseMode): SandboxFieldDef[] {
    const d = defaultModeParams(baseMode);
    const fields: SandboxFieldDef[] = [];
    for (const [key, val] of Object.entries(d)) {
        const path = `mode.${key}`;
        const hint = sandboxFieldHelp(path, key);
        if (typeof val === "boolean") {
            fields.push({ kind: "toggle", key: path, label: labelize(key), hint });
        } else if (typeof val === "number") {
            const { min, max, step } = guessRange(key, val);
            fields.push({ kind: "number", key: path, label: labelize(key), min, max, step, hint });
        }
    }
    return fields;
}

function labelize(key: string): string {
    return key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (c) => c.toUpperCase())
        .replace(/Ms\b/g, "(ms)")
        .replace(/PxS\b/g, "(px/s)")
        .replace(/Frac\b/g, "(frac)");
}

function guessRange(key: string, def: number): { min: number; max: number; step: number } {
    const k = key.toLowerCase();
    if (k.includes("lives") || k === "livesstart" || k === "livescap" || k === "livesmax") {
        return { min: 1, max: 1000, step: 1 };
    }
    if (k === "lifebonusevery") return { min: 100, max: 100000, step: 50 };
    if (k.includes("ms")) return { min: 100, max: 120000, step: 100 };
    if (k.includes("frac")) return { min: 0.01, max: 1, step: 0.01 };
    if (k.includes("boost") || k.includes("mult") || k.includes("rate")) {
        return { min: 0, max: 1000, step: 0.01 };
    }
    if (k.includes("accel") || k.includes("drag") || k.includes("angular")) {
        return { min: 0, max: 1000, step: 0.1 };
    }
    if (k.includes("speed")) return { min: 0, max: 1000, step: def < 5 ? 0.05 : 0.1 };
    if (k.includes("gap") || k.includes("radius") || k.includes("size") || k.includes("clearance")) {
        return { min: 0, max: 1000, step: Number.isInteger(def) ? 1 : 0.1 };
    }
    if (Number.isInteger(def)) return { min: 0, max: Math.max(1000, def * 50), step: 1 };
    return { min: 0, max: Math.max(1000, def * 50), step: 0.1 };
}

export function getPath(cfg: SandboxConfig, path: string): number | boolean | string | unknown {
    const [root, ...rest] = path.split(".");
    if (root === "common") {
        const k = rest[0] as keyof SandboxConfig["common"];
        return cfg.common[k];
    }
    if (root === "mode") {
        return cfg.mode[rest[0]!];
    }
    return undefined;
}

export function setPath(cfg: SandboxConfig, path: string, value: number | boolean): SandboxConfig {
    const next = structuredClone(cfg) as SandboxConfig;
    const [root, ...rest] = path.split(".");
    if (root === "common") {
        const k = rest[0] as keyof SandboxConfig["common"];
        (next.common as Record<string, unknown>)[k] = value;
    } else if (root === "mode") {
        next.mode[rest[0]!] = value;
    }
    return normalizeSandboxConfig(next);
}

/**
 * Normalize any stored preset (v1 legacy or v2) into SandboxConfig.
 */
export function normalizeSandboxConfig(raw: unknown): SandboxConfig {
    const obj = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};

    // Legacy v1 flat shape (no schemaVersion / no baseMode nested)
    const isLegacy =
        obj.schemaVersion == null &&
        (obj.ballsSize != null || obj.ballsSpeed != null || obj.speedrunMode != null) &&
        obj.common == null;

    // Runtime flat shape used by legacy-app (`sandboxRuntimeFlat`): schemaVersion 2 +
    // common fields at the top level (no nested `common`). Must not drop toggles.
    const isFlatRuntime =
        obj.common == null &&
        (obj.ballsSize != null ||
            obj.ballsSpeed != null ||
            typeof obj.wasdEnabled === "boolean" ||
            typeof obj.ballCollisionsEnabled === "boolean" ||
            typeof obj.magnetEnabled === "boolean");

    let baseMode: SandboxBaseMode = "normal";
    if (obj.baseMode === "overload") {
        // Overload mode was removed; migrate old presets to Normal.
        baseMode = "normal";
    } else if (typeof obj.baseMode === "string" && isSandboxBaseMode(obj.baseMode)) {
        baseMode = obj.baseMode;
    } else if (isLegacy) {
        // Infer from legacy toggles
        if (obj.speedrunMode) baseMode = "speedrun";
        else if (obj.wasdEnabled) baseMode = "wasd";
        else if (obj.progressionEnabled) baseMode = "progression";
        else baseMode = "normal";
    }

    const defaults = defaultSandboxConfig(baseMode);
    const commonIn = (
        obj.common && typeof obj.common === "object"
            ? obj.common
            : isLegacy || isFlatRuntime
              ? obj
              : {}
    ) as Record<string, unknown>;
    const modeIn = (obj.mode && typeof obj.mode === "object" ? obj.mode : {}) as Record<string, unknown>;

    let boost = num(commonIn.bounceSpeedBoost, defaults.common.bounceSpeedBoost);
    if (boost > 0 && boost < 1.1 && isLegacy) boost = 1.1;

    const common: SandboxConfig["common"] = {
        ballsSize: clamp(num(commonIn.ballsSize, defaults.common.ballsSize), 1, 1000),
        ballsSpeed: clamp(num(commonIn.ballsSpeed, defaults.common.ballsSpeed), 0.1, 1000),
        initialBalls: clamp(num(commonIn.initialBalls, defaults.common.initialBalls), 0, 500),
        ballsPerSpawn: clamp(num(commonIn.ballsPerSpawn, defaults.common.ballsPerSpawn), 0, 1000),
        pointsPerSpawn: clamp(num(commonIn.pointsPerSpawn, defaults.common.pointsPerSpawn), 1, 10000),
        cursorSpeed: clamp(num(commonIn.cursorSpeed, defaults.common.cursorSpeed), 1, 1000),
        cursorSize: clamp(num(commonIn.cursorSize, defaults.common.cursorSize), 1, 1000),
        smoothCursor: clamp(num(commonIn.smoothCursor, defaults.common.smoothCursor), 0, 1000),
        ballCollisionsEnabled: !!(commonIn.ballCollisionsEnabled ?? defaults.common.ballCollisionsEnabled),
        bounceSpeedBoost: clamp(boost, 0, 1000),
        wasdEnabled: !!(commonIn.wasdEnabled ?? defaults.common.wasdEnabled),
        progressionEnabled: !!(commonIn.progressionEnabled ?? defaults.common.progressionEnabled),
        magnetEnabled: !!(commonIn.magnetEnabled ?? defaults.common.magnetEnabled),
        spawnClearance: clamp(num(commonIn.spawnClearance, defaults.common.spawnClearance), 0, 1000),
        // SpeedRun Layer UI removed: always on for SpeedRun base, always off otherwise.
        speedrunMode: baseMode === "speedrun",
        speedrunLayout: normalizeLevelData(commonIn.speedrunLayout ?? defaults.common.speedrunLayout),
        penaltyLayout: normalizePenaltyLevelData(commonIn.penaltyLayout ?? defaults.common.penaltyLayout),
    };

    const mode: Record<string, number | boolean | string> = { ...defaults.mode };
    for (const [k, v] of Object.entries(modeIn)) {
        if (typeof v === "boolean" || typeof v === "string") mode[k] = v;
        else if (typeof v === "number" && Number.isFinite(v)) mode[k] = v;
        else if (v != null && Number.isFinite(Number(v))) mode[k] = Number(v);
    }

    // Keep overlapping numeric knobs in sync: mode values win (what the player edited in Mode parameters).
    syncOverlapping(common, mode);

    return { schemaVersion: 2, baseMode, common, mode };
}

function syncOverlapping(common: SandboxConfig["common"], mode: Record<string, number | boolean | string>) {
    const map: Array<[keyof SandboxConfig["common"], string]> = [
        ["ballsSpeed", "ballsSpeed"],
        ["ballsSize", "ballsSize"],
        ["cursorSize", "cursorSize"],
        ["initialBalls", "initialBalls"],
        ["ballsPerSpawn", "ballsPerSpawn"],
        ["pointsPerSpawn", "pointsPerSpawn"],
        ["cursorSpeed", "cursorSpeed"],
    ];
    for (const [ck, mk] of map) {
        const mv = mode[mk];
        if (typeof mv === "number" && Number.isFinite(mv)) {
            (common as Record<string, unknown>)[ck] = mv;
        } else if (typeof common[ck] === "number") {
            mode[mk] = common[ck] as number;
        }
    }
    // Modes that use `ballSpeed` (Color Rush, Counter, …) — keep common.ballsSpeed aligned for magnet/overlays.
    const modeBallSpeed = mode.ballSpeed;
    if (typeof modeBallSpeed === "number" && Number.isFinite(modeBallSpeed)) {
        common.ballsSpeed = modeBallSpeed;
    }
}

/** Default value for a single form field path (e.g. `common.magnetEnabled` or `mode.ballSpeed`). */
export function getDefaultFieldValue(baseMode: SandboxBaseMode, fieldPath: string): unknown {
    return getPath(defaultSandboxConfig(baseMode), fieldPath);
}

/** Flat wire shape for multiplayer (backward compatible + baseMode). */
export function toMpSandboxWire(cfg: SandboxConfig) {
    return {
        baseMode: cfg.baseMode,
        ballsSize: cfg.common.ballsSize,
        ballsSpeed: cfg.common.ballsSpeed,
        initialBalls: cfg.common.initialBalls,
        ballsPerSpawn: cfg.common.ballsPerSpawn,
        pointsPerSpawn: cfg.common.pointsPerSpawn,
        bounceSpeedBoost: cfg.common.bounceSpeedBoost,
        cursorSpeed: cfg.common.cursorSpeed,
        cursorSize: cfg.common.cursorSize,
        smoothCursor: cfg.common.smoothCursor,
        ballCollisionsEnabled: cfg.common.ballCollisionsEnabled,
        wasdEnabled: cfg.common.wasdEnabled,
        progressionEnabled: cfg.common.progressionEnabled,
        magnetEnabled: cfg.common.magnetEnabled,
        spawnClearance: cfg.common.spawnClearance,
        mode: cfg.mode,
        schemaVersion: 2 as const,
    };
}

export function fromMpSandboxWire(raw: unknown): SandboxConfig {
    const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    return normalizeSandboxConfig({
        schemaVersion: 2,
        baseMode: o.baseMode,
        common: {
            ballsSize: o.ballsSize,
            ballsSpeed: o.ballsSpeed,
            initialBalls: o.initialBalls,
            ballsPerSpawn: o.ballsPerSpawn,
            pointsPerSpawn: o.pointsPerSpawn,
            bounceSpeedBoost: o.bounceSpeedBoost,
            cursorSpeed: o.cursorSpeed,
            cursorSize: o.cursorSize,
            smoothCursor: o.smoothCursor,
            ballCollisionsEnabled: o.ballCollisionsEnabled,
            wasdEnabled: o.wasdEnabled,
            progressionEnabled: o.progressionEnabled,
            magnetEnabled: o.magnetEnabled,
            spawnClearance: o.spawnClearance,
            speedrunMode: o.baseMode === "speedrun",
            speedrunLayout: null,
            penaltyLayout: o.penaltyLayout ?? null,
        },
        mode: o.mode,
    });
}
